/**
 * Visual Effects
 * Handles screen flash, background, shockwave, and camera shake
 *
 * Note: All animations use Web Animations API / CSS transitions
 * No GSAP dependency - pure browser APIs for better performance
 */

import { logger } from '@/utils/logger.js';
import type { EffectElements, ColorPalettes, BackgroundState, FlashControl, Mood } from './types.js';

/**
 * Easing functions for animations
 */
const easing = {
  easeOutQuad: (t: number) => 1 - Math.pow(1 - t, 2),
  easeInOutQuad: (t: number) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,
  easeOutPower2: (t: number) => 1 - Math.pow(1 - t, 2),
};

/**
 * Animate element property using requestAnimationFrame
 */
function animateElement(
  element: HTMLElement,
  fromProps: Record<string, number>,
  toProps: Record<string, number>,
  duration: number,
  easingFn: (t: number) => number = easing.easeOutQuad,
  onComplete?: () => void
): void {
  const startTime = performance.now();
  const propNames = Object.keys(toProps);

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easingFn(progress);

    for (const prop of propNames) {
      const from = fromProps[prop] ?? 0;
      const to = toProps[prop];
      const current = from + (to - from) * eased;

      if (prop === 'opacity') {
        element.style.opacity = String(current);
      } else if (prop === 'x' || prop === 'y') {
        // Handle transform properties
        const currentTransform = element.style.transform || '';
        const regex = prop === 'x' ? /translateX\([^)]*\)/ : /translateY\([^)]*\)/;
        const newValue = `translate${prop.toUpperCase()}(${current}px)`;
        if (regex.test(currentTransform)) {
          element.style.transform = currentTransform.replace(regex, newValue);
        } else {
          element.style.transform = `${currentTransform} ${newValue}`.trim();
        }
      } else if (prop === 'width' || prop === 'height') {
        element.style[prop] = `${current}px`;
      } else if (prop === 'borderWidth') {
        element.style.borderWidth = `${current}px`;
      }
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Trigger screen flash
 */
export function triggerScreenFlash(
  intensity: number,
  mood: Mood,
  effectElements: EffectElements,
  colorPalettes: ColorPalettes,
  flashControl: FlashControl,
  ensureElementExists: (elementType: keyof EffectElements) => void
): void {
  const now = Date.now();
  if (now - flashControl.lastFlash < flashControl.minInterval) {
    return;
  }

  ensureElementExists('screenFlash');

  if (!effectElements.screenFlash) {
    logger.warn('⚠️ Screen flash element could not be created');
    return;
  }

  const colors = colorPalettes[mood] || colorPalettes.normal;
  const flashColor = colors[Math.floor(Math.random() * colors.length)];
  const opacity = Math.max(0.3, Math.min(flashControl.maxIntensity, 0.3 + (intensity * 0.5)));

  // 🎨 サイバーパンク風フラッシュ: ネオングロウ + スキャンライン風
  effectElements.screenFlash.style.background =
    `radial-gradient(ellipse at center, ${flashColor}80 0%, ${flashColor}40 30%, transparent 70%)`;

  const animationDuration = 300 + (intensity * 200);
  animateElement(
    effectElements.screenFlash,
    { opacity },
    { opacity: 0 },
    animationDuration,
    easing.easeOutQuad
  );

  flashControl.lastFlash = now;
}

/**
 * Generate dynamic gradient (Cyberpunk style)
 * 🎨 サイバーパンク風のグラデーション - 暗い背景にネオンカラーのグロウ
 */
export function generateDynamicGradient(colors: string[], intensity: number): string {
  const primaryColor = colors[Math.floor(Math.random() * colors.length)];
  const secondaryColor = colors[Math.floor(Math.random() * colors.length)];
  const angle = 45 + (intensity * 90);

  // サイバーパンク風: 暗い背景 + ネオンカラーのグロウ効果
  return `linear-gradient(${angle}deg,
    rgba(39, 39, 42, 0.9) 0%,
    ${primaryColor}33 30%,
    ${secondaryColor}33 70%,
    rgba(39, 39, 42, 0.9) 100%)`;
}

/**
 * Update background mood
 */
export function updateBackgroundMood(
  mood: Mood,
  intensity: number,
  effectElements: EffectElements,
  colorPalettes: ColorPalettes,
  backgroundState: BackgroundState,
  ensureElementExists: (elementType: keyof EffectElements) => void
): void {
  if (backgroundState.isAnimating) {
    return;
  }

  ensureElementExists('background');

  if (!effectElements.background) {
    logger.warn('⚠️ Background element could not be created');
    return;
  }

  const colors = colorPalettes[mood] || colorPalettes.normal;
  const gradient = generateDynamicGradient(colors, intensity);

  backgroundState.isAnimating = true;

  // Set background immediately (CSS can't animate gradient changes smoothly)
  effectElements.background.style.background = gradient;

  // Animate opacity
  const currentOpacity = parseFloat(effectElements.background.style.opacity) || 0;
  const targetOpacity = 0.4 + (intensity * 0.4);

  animateElement(
    effectElements.background,
    { opacity: currentOpacity },
    { opacity: targetOpacity },
    800,
    easing.easeInOutQuad,
    () => {
      backgroundState.isAnimating = false;
    }
  );

  backgroundState.currentGradient = mood;
}

/**
 * Trigger shockwave
 */
export function triggerShockwave(
  intensity: number,
  effectElements: EffectElements,
  ensureElementExists: (elementType: keyof EffectElements) => void
): void {
  ensureElementExists('shockwave');

  if (!effectElements.shockwave) {
    logger.warn('⚠️ Shockwave element could not be created');
    return;
  }

  const size = 200 + (intensity * 300);
  const durationMs = (0.6 + (intensity * 0.4)) * 1000;
  const borderWidth = 2 + intensity * 3;

  // Set initial state
  effectElements.shockwave.style.width = '0px';
  effectElements.shockwave.style.height = '0px';
  effectElements.shockwave.style.opacity = '0.8';
  effectElements.shockwave.style.borderWidth = `${borderWidth}px`;

  // Animate expansion
  animateElement(
    effectElements.shockwave,
    { width: 0, height: 0, opacity: 0.8 },
    { width: size, height: size, opacity: 0 },
    durationMs,
    easing.easeOutQuad
  );
}

/**
 * Apply camera shake
 */
export function applyCameraShake(intensity: number, _mood: Mood): void {
  if (intensity < 0.3) {
    logger.debug(`📳 applyCameraShake skipped - intensity ${intensity.toFixed(2)} < 0.3`);
    return;
  }

  const shakeAmount = intensity * 8;
  const durationMs = (0.3 + (intensity * 0.2)) * 1000;

  const targetElement = document.getElementById('tokchan-display') ||
                       document.querySelector<HTMLElement>('.character-container');

  if (!targetElement) {
    logger.warn('⚠️ applyCameraShake: No target element found (tokchan-display or .character-container)');
    return;
  }

  logger.debug(`📳 applyCameraShake executing - shakeAmount: ${shakeAmount.toFixed(2)}, duration: ${durationMs}ms`);

  const shakeInterval = 50; // 50ms per shake
  const shakeCount = Math.floor(durationMs / shakeInterval);
  let currentShake = 0;

  const originalTransform = targetElement.style.transform || '';

  const shake = () => {
    if (currentShake >= shakeCount) {
      // Reset to original position
      targetElement.style.transform = originalTransform;
      return;
    }

    const offsetX = (Math.random() - 0.5) * shakeAmount;
    const offsetY = (Math.random() - 0.5) * shakeAmount;

    // Preserve existing transform and add shake
    const baseTransform = originalTransform ? `${originalTransform} ` : '';
    targetElement.style.transform = `${baseTransform}translate(${offsetX}px, ${offsetY}px)`;

    currentShake++;
    setTimeout(shake, shakeInterval);
  };

  shake();
}

/**
 * Trigger sharp flash (for snare)
 */
export function triggerSharpFlash(
  intensity: number,
  mood: Mood,
  effectElements: EffectElements,
  colorPalettes: ColorPalettes,
  ensureElementExists: (elementType: keyof EffectElements) => void
): void {
  ensureElementExists('screenFlash');

  if (!effectElements.screenFlash) {
    logger.warn('⚠️ Screen flash element could not be created for sharp flash');
    return;
  }

  const colors = colorPalettes[mood] || colorPalettes.normal;
  const flashColor = colors[Math.floor(Math.random() * colors.length)];
  const startOpacity = 0.6 * intensity;

  effectElements.screenFlash.style.background = flashColor;

  animateElement(
    effectElements.screenFlash,
    { opacity: startOpacity },
    { opacity: 0 },
    150,
    easing.easeOutQuad
  );
}

/**
 * Trigger color strobe
 */
export function triggerColorStrobe(
  intensity: number,
  mood: Mood,
  effectElements: EffectElements,
  colorPalettes: ColorPalettes
): void {
  const colors = colorPalettes[mood] || colorPalettes.normal;
  const strobeCount = Math.floor(2 + intensity * 4);

  for (let i = 0; i < strobeCount; i++) {
    setTimeout(() => {
      if (!effectElements.screenFlash) return;

      const color = colors[i % colors.length];
      effectElements.screenFlash.style.background = color;

      animateElement(
        effectElements.screenFlash,
        { opacity: 0.2 },
        { opacity: 0 },
        100,
        easing.easeOutQuad
      );
    }, i * 100);
  }
}

/**
 * Get mood-based filters (Cyberpunk unified)
 * 🎨 サイバーパンク風フィルター - 全ムード統一
 */
export function getMoodFilters(_mood: Mood, intensity: number): string {
  // サイバーパンク統一フィルター: 高コントラスト + ネオングロウ
  const hueShift = Math.sin(Date.now() / 1000) * 30; // 動的な色相シフト
  return `hue-rotate(${hueShift + intensity * 45}deg) saturate(${1.4 + intensity * 0.6}) brightness(${1.1 + intensity * 0.4}) contrast(${1.1 + intensity * 0.2})`;
}
